import parser from 'any-date-parser';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Grid,
  Group,
  NumberInput,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { closeModal } from '@mantine/modals';
import { handleSubmitError } from '@utils/forms';

import {
  BookingPartecipant,
  UpdateBookingPartecipantRequest,
} from '@interfaces/booking.interface';

import { useUpdateBookingPartecipantMutation } from '@api/bookings.api';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useFormEnhanced from '@hooks/useFormEnhanced';

import {
  CitySelector,
  NationSelector,
  ProvinceSelector,
  ZipSelector,
} from './common/locations';

interface UpdatePartecipantProps {
  data: BookingPartecipant;
}
export const UPDATE_PARTECIPANT_FORM_MODAL_ID = 'UpdatePartecipant';

export default function UpdatePartecipant({ data }: UpdatePartecipantProps) {
  // ==========================================================================
  // General
  // ==========================================================================
  const [params] = useQueryParams();
  const bookingId = params.get('id');
  const { t } = useTranslation();

  // ==========================================================================
  // State
  // ==========================================================================

  // ==========================================================================
  // Api
  // ==========================================================================
  const [UpdateBookingPartecipant, { isLoading: isLoadingUpdate }] =
    useUpdateBookingPartecipantMutation();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    email: data?.email,
    height: data?.height,
    birthDate: data?.birthDate
      ? new Date(data.birthDate).toISOString()
      : undefined,
    birthCity: data?.birthCity,
    country: data?.country,
    taxCode: data?.taxCode,
    phone: data?.phone,
    province: data?.province,
    city: data?.city,
    zip: data?.zip,
    address: data?.address,
    notes: data?.notes,
  };

  const form = useFormEnhanced({
    mode: 'uncontrolled',
    initialValues,
    normalizeFields: {
      capitalizeFields: ['birthCity', 'address'],
      lowercaseFields: ['email'],
      uppercaseFields: ['taxCode'],
    },
  });

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const onSubmit = async (values: typeof initialValues) => {
    try {
      const updateBookingPartecipantRequest: UpdateBookingPartecipantRequest = {
        id: +bookingId!,
        partecipantId: data.id,
        body: {
          email: values.email!,
          height: values.height!,
          birthDate: new Date(values.birthDate!).toISOString(),
          birthCity: values.birthCity!,
          country: values.country!,
          province: values.province,
          city: values.city!,
          zip: values.zip!,
          address: values.address,
          notes: values.notes!,
          phone: values.phone,
          taxCode: values.taxCode,
        },
      };

      await UpdateBookingPartecipant(updateBookingPartecipantRequest).unwrap();

      closeModal(UPDATE_PARTECIPANT_FORM_MODAL_ID);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  const onCancelClick = () => {
    closeModal(UPDATE_PARTECIPANT_FORM_MODAL_ID);
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label={t('bookings.participant.fields.firstName.label')}
            key={form.key('firstName')}
            required
            value={data.firstName}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label={t('bookings.participant.fields.lastName.label')}
            key={form.key('lastName')}
            required
            value={data.lastName}
          />
        </Grid.Col>
        <Grid.Col span={{ md: 6 }}>
          <TextInput
            label={t('bookings.participant.fields.email.label')}
            placeholder={t('bookings.participant.fields.email.placeholder')}
            key={form.key('email')}
            required
            {...form.getInputProps('email')}
          />
        </Grid.Col>
        {form.values.country === 'Italia' && (
          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label={t('bookings.participant.fields.taxCode.label')}
              placeholder={t('bookings.participant.fields.taxCode.placeholder')}
              key={form.key('taxCode')}
              required
              {...form.getInputProps('taxCode')}
            />
          </Grid.Col>
        )}
        <Grid.Col span={{ md: 6 }}>
          <TextInput
            label={t('bookings.participant.fields.phone.label')}
            placeholder={t('bookings.participant.fields.phone.placeholder')}
            key={form.key('phone')}
            required
            {...form.getInputProps('phone')}
          />
        </Grid.Col>
        <Grid.Col span={{ md: 6 }}>
          <NumberInput
            label={t('bookings.participant.fields.height.label')}
            placeholder={t('bookings.participant.fields.height.placeholder')}
            key={form.key('height')}
            required
            min={50}
            {...form.getInputProps('height')}
          />
        </Grid.Col>
        <Grid.Col span={{ md: 6 }}>
          <DateInput
            label={t('bookings.participant.fields.birthDate.label')}
            placeholder={t('bookings.participant.fields.birthDate.placeholder')}
            key={form.key('birthDate')}
            required
            valueFormat="DD/MM/YYYY"
            dateParser={(value) => parser.fromString(value, 'it')}
            {...form.getInputProps('birthDate')}
          />
        </Grid.Col>{' '}
        <Grid.Col span={{ md: 6 }}>
          <TextInput
            label={t('bookings.participant.fields.birthCity.label')}
            placeholder={t('bookings.participant.fields.birthCity.placeholder')}
            key={form.key('birthCity')}
            required
            {...form.getInputProps('birthCity')}
          />
        </Grid.Col>
        <Grid.Col span={{ md: 6 }}>
          <NationSelector
            placeholder={t('bookings.participant.fields.country.placeholder')}
            key={form.key('country')}
            required
            {...form.getInputProps('country')}
          />
        </Grid.Col>
        <Grid.Col span={{ md: 6 }}>
          {form.values.country === 'Italia' ? (
            <ProvinceSelector
              key={form.key('province')}
              required
              placeholder={t(
                'bookings.participant.fields.province.placeholder',
              )}
              {...form.getInputProps('province')}
            />
          ) : (
            <TextInput
              label={t('bookings.participant.fields.city.label')}
              placeholder={t('bookings.participant.fields.city.placeholder')}
              key={form.key('city')}
              required
              {...form.getInputProps('city')}
            />
          )}
        </Grid.Col>
        {form.values.country === 'Italia' && (
          <>
            <Grid.Col span={{ md: 6 }}>
              <CitySelector
                key={form.key('city')}
                required
                placeholder={t('bookings.participant.fields.city.placeholder')}
                province={form.values.province || ''}
                {...form.getInputProps('city')}
              />
            </Grid.Col>
            <Grid.Col span={{ md: 6 }}>
              <ZipSelector
                key={form.key('zip')}
                required
                placeholder={t('bookings.participant.fields.zip.placeholder')}
                city={form.values.city || ''}
                {...form.getInputProps('zip')}
              />
            </Grid.Col>
          </>
        )}
        <Grid.Col span={{ md: 6 }}>
          <TextInput
            label={t('bookings.participant.fields.address.label')}
            placeholder={t('bookings.participant.fields.address.placeholder')}
            key={form.key('address')}
            required
            {...form.getInputProps('address')}
          />
        </Grid.Col>
        <Grid.Col span={{ md: 6 }}>
          <Textarea
            label={t('bookings.participant.fields.notes.label')}
            placeholder={t('bookings.participant.fields.notes.placeholder')}
            key={form.key('notes')}
            {...form.getInputProps('notes')}
          />
        </Grid.Col>
      </Grid>

      {form.errors.general && (
        <Text c="red" size="xs" ta="left" mt="lg">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingUpdate}
        >
          {t('bookings.participant.actions.cancel')}
        </Button>
        <Button type="submit" loading={isLoadingUpdate}>
          {t('bookings.participant.actions.confirm')}
        </Button>
      </Group>
    </form>
  );
}
